import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * Find the actual text node used for a product title.
 * Product cards render a `.text-block` wrapper with a heading/paragraph child.
 * @param {ParentNode} root
 * @returns {HTMLElement | null}
 */
function findTitleElement(root) {
  return /** @type {HTMLElement | null} */ (
    root.querySelector('.title-text, .text-block > *:first-child, .text-block') || null
  );
}

/**
 * For product cards, display only the first and last keyword joined by a space.
 * The original full title remains available in data attributes and server HTML.
 * @param {HTMLElement} textElement
 */
function applyCardTitleDisplay(textElement) {
  if (!textElement.closest('.product-card')) return;

  const sourceTitle = textElement.dataset.fullTitle || textElement.textContent?.trim() || '';
  if (!sourceTitle) return;

  textElement.dataset.fullTitle = sourceTitle;

  const keywords = sourceTitle.split(/\s+/).filter(Boolean);
  textElement.textContent = keywords.length <= 1 ? sourceTitle : `${keywords[0]} ${keywords[keywords.length - 1]}`;
}

/**
 * Apply line clamp based on available height.
 * @param {HTMLElement} host
 * @param {HTMLElement} textElement
 */
function applyLineClamp(host, textElement) {
  if (host.closest('.product-card')) {
    return;
  }

  const containerHeight = host.clientHeight;
  if (!containerHeight) return;

  const computedStyle = window.getComputedStyle(host);
  const lineHeight = parseFloat(computedStyle.lineHeight);
  const paddingTop = parseFloat(computedStyle.paddingTop);
  const paddingBottom = parseFloat(computedStyle.paddingBottom);
  const availableHeight = containerHeight - paddingTop - paddingBottom;
  const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

  textElement.style.display = '-webkit-box';
  textElement.style.webkitBoxOrient = 'vertical';
  textElement.style.overflow = 'hidden';
  textElement.style.textOverflow = 'ellipsis';
  textElement.style.webkitLineClamp = String(maxLines);
}

/**
 * Handle title formatting and truncation for a standalone host.
 * @param {HTMLElement} host
 */
function formatTitleHost(host) {
  if (host.closest('.product-card')) return;

  const textElement = findTitleElement(host);
  if (!textElement || !textElement.textContent) return;

  applyCardTitleDisplay(textElement);
  applyLineClamp(host, textElement);
}

/**
 * A component that handles title truncation when a <product-title> custom element exists.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        formatTitleHost(this);
      });

      this.resizeObserver.observe(this);
      formatTitleHost(this);
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize);
      formatTitleHost(this);
    }
  }

  #handleResize = () => {
    formatTitleHost(this);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

const observedCardTitles = new WeakSet();
let cardTitleResizeObserver = null;

/**
 * Initialize product card titles rendered without a <product-title> custom element.
 * @param {ParentNode} root
 */
function initProductCardTitles(root = document) {
  return;
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initProductCardTitles(document);
    },
    { once: true }
  );
} else {
  initProductCardTitles(document);
}

if ('MutationObserver' in window) {
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches('.product-card [ref="productTitleLink"] .text-block')) return;
        if (node.querySelector('.product-card [ref="productTitleLink"] .text-block')) return;
      });
    });
  });

  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

export default ProductTitle;
